/**
 * FieldError — React (web) implementation.
 *
 * Renders nothing without a message. The row carries `aria-live="polite"`
 * (ADR-025 Rule 4), but a live region that appears together with its text is
 * not reliably announced, so a field that shows errors should keep an
 * always-present container around this row and let the row appear inside it
 * (PhoneInput and CheckboxField do).
 */

import React from 'react';
import { preventOrphan } from '@galapagos/shared';
import { Icon } from '../Icon/Icon.web';
import {
  hasFieldErrorMessage,
  FIELD_ERROR_ICON,
  FIELD_ERROR_ICON_SIZE,
  FIELD_ERROR_ICON_COLOR,
  FIELD_ERROR_TEXT_COLOR,
  FIELD_ERROR_TYPOGRAPHY,
  FIELD_ERROR_GAP,
} from './FieldError';

export interface WebFieldErrorProps {
  message?: string;
  id?: string;
  /** Set false when a parent already wraps this row in a polite live region,
   *  so the message is not announced twice. Defaults to true. */
  live?: boolean;
  'data-testid'?: string;
}

export function FieldError({
  message,
  id,
  live = true,
  'data-testid': testId,
}: WebFieldErrorProps) {
  if (!hasFieldErrorMessage(message)) return null;

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: FIELD_ERROR_GAP,
  };

  const messageStyle: React.CSSProperties = {
    fontFamily: FIELD_ERROR_TYPOGRAPHY.fontFamily,
    fontWeight: FIELD_ERROR_TYPOGRAPHY.fontWeight,
    fontSize: FIELD_ERROR_TYPOGRAPHY.fontSize,
    lineHeight: `${FIELD_ERROR_TYPOGRAPHY.lineHeight}px`,
    letterSpacing: FIELD_ERROR_TYPOGRAPHY.letterSpacing,
    color: FIELD_ERROR_TEXT_COLOR,
  };

  return (
    <div id={id} aria-live={live ? 'polite' : undefined} style={rowStyle} data-testid={testId}>
      <Icon
        name={FIELD_ERROR_ICON}
        size={FIELD_ERROR_ICON_SIZE}
        color={FIELD_ERROR_ICON_COLOR}
        aria-hidden
      />
      <span style={messageStyle} data-testid={testId ? `${testId}-message` : undefined}>
        {preventOrphan(message)}
      </span>
    </div>
  );
}
